// Sparkline — a tiny inline-SVG trend line for KPI strips. No chart libraries.
// Colorblind-safe: direction is encoded by the petrol (up/neutral) vs clay
// (down) stroke token, never red/green alone.

import { useState } from 'react'
import { CfoChartFrame, useChartTooltip } from './chartInteraction'

/** A reference rule under the series — an average, a percentile, an SD band. */
export type SparklineReference = {
  value: number
  /** Bounds of a band around `value`; both are required for the band to draw. */
  upper?: number | null
  lower?: number | null
  className?: string
}

export type SparklineProps = {
  values: number[]
  /** Force the "down" (clay) treatment regardless of slope. */
  down?: boolean
  /** Hover a point to read its value. Wraps the SVG in a positioned tooltip frame. */
  interactive?: boolean
  /** Per-point labels for the tooltip's second line (e.g. "Mar 2026"). Indexes align with `values`. */
  labels?: string[]
  /** Formats the tooltip's headline number. Defaults to the raw value. */
  formatValue?: (value: number) => string
  /** Drop both tones for an anchor-grey line — for metrics with no good direction (DPO, A/P turnover). */
  neutral?: boolean
  /** Draw a faint dashed reference line at this value (a ratio benchmark). Folded into the
   *  vertical domain so the line is always on-canvas, even well outside the series range. */
  benchmark?: number | null
  /** Additional reference rules (analysis reference lines), each optionally a band.
   *  Drawn subordinate to both the series and the benchmark, and folded into the
   *  vertical domain for the same reason the benchmark is. */
  references?: SparklineReference[] | null
  /** Mark the last point with a filled dot so the end value is locatable at a glance. The dot is
   *  drawn as a circle under `preserveAspectRatio="none"`, so give the SVG a CSS box matching
   *  `width`/`height` (the cockpit cell does) or it renders as an ellipse. */
  endDot?: boolean
  width?: number
  height?: number
  ariaLabel?: string
  className?: string
}

/** Map a numeric series to `x,y` SVG polyline points in a 0..width/height box. */
export function sparklinePoints(values: number[], width = 100, height = 30): string {
  const finite = values.filter((v) => Number.isFinite(v))
  if (finite.length < 2) return ''
  const min = Math.min(...finite)
  const max = Math.max(...finite)
  const span = max - min || 1
  const stepX = width / (finite.length - 1)
  return finite
    .map((v, i) => {
      const x = i * stepX
      const y = height - ((v - min) / span) * (height - 2) - 1
      return `${x.toFixed(2)},${y.toFixed(2)}`
    })
    .join(' ')
}

/** Shared vertical scale for the series AND an optional reference value. Kept separate from
 *  `sparklinePoints` so the benchmark can widen the domain without changing that helper's
 *  long-standing contract (it is reused verbatim by the health rows and ratio score cards). */
export function sparklineScale(
  values: number[],
  height: number,
  benchmark?: number | null,
  /** Further values the axis must contain (reference rules and their bounds). */
  extraDomain?: Array<number | null | undefined>,
): (v: number) => number {
  const domain = values.filter((v) => Number.isFinite(v))
  if (benchmark != null && Number.isFinite(benchmark)) domain.push(benchmark)
  for (const extra of extraDomain || []) {
    if (extra != null && Number.isFinite(extra)) domain.push(extra)
  }
  const min = Math.min(...domain)
  const max = Math.max(...domain)
  const span = max - min || 1
  return (v: number) => height - ((v - min) / span) * (height - 2) - 1
}

export function Sparkline({
  values,
  down,
  neutral,
  benchmark,
  references,
  endDot,
  interactive,
  labels,
  formatValue,
  width = 100,
  height = 30,
  ariaLabel,
  className,
}: SparklineProps) {
  const { containerRef, tip, show, hide } = useChartTooltip()
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)
  const finite = values.filter((v) => Number.isFinite(v))
  if (finite.length < 2) return null

  const hasBenchmark = benchmark != null && Number.isFinite(benchmark)
  const refs = (references || []).filter((reference) => Number.isFinite(reference.value))
  const y = sparklineScale(
    finite,
    height,
    hasBenchmark ? benchmark : null,
    refs.flatMap((reference) => [reference.value, reference.upper, reference.lower]),
  )
  const stepX = width / (finite.length - 1)
  const points = finite
    .map((v, i) => `${(i * stepX).toFixed(2)},${y(v).toFixed(2)}`)
    .join(' ')

  const isDown = down ?? finite[finite.length - 1] < finite[0]
  const stroke = neutral
    ? 'var(--anchor, #9aa0a8)'
    : isDown
      ? 'var(--neg, #c0492f)'
      : 'var(--pos, #0e7c86)'

  const fmt = formatValue ?? ((v: number) => String(v))
  const pointAt = (i: number) => ({ x: i * stepX, y: y(finite[i]) })

  const svg = (
    <svg
      className={className}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={ariaLabel || 'Trend sparkline'}
    >
      {hasBenchmark && (
        <line
          x1={0}
          y1={y(benchmark as number)}
          x2={width}
          y2={y(benchmark as number)}
          stroke="var(--anchor, #9aa0a8)"
          strokeOpacity={0.55}
          strokeWidth={1}
          strokeDasharray="2 3"
        />
      )}
      {refs.map((reference, i) => {
        const banded =
          reference.upper != null &&
          reference.lower != null &&
          Number.isFinite(reference.upper) &&
          Number.isFinite(reference.lower)
        const bandTop = banded ? y(Math.max(reference.upper as number, reference.lower as number)) : 0
        const bandBottom = banded ? y(Math.min(reference.upper as number, reference.lower as number)) : 0
        return (
          <g key={`ref-${i}`} className={reference.className}>
            {banded ? (
              <rect
                x={0}
                y={bandTop}
                width={width}
                height={Math.max(0.5, bandBottom - bandTop)}
                fill="var(--anchor, #9aa0a8)"
                fillOpacity={0.12}
              />
            ) : null}
            <line
              x1={0}
              y1={y(reference.value)}
              x2={width}
              y2={y(reference.value)}
              stroke="var(--anchor, #9aa0a8)"
              strokeOpacity={0.45}
              strokeWidth={1}
              strokeDasharray="4 3"
            />
          </g>
        )
      })}
      <polyline fill="none" stroke={stroke} strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" points={points} />
      {endDot && (
        <circle cx={width} cy={y(finite[finite.length - 1])} r={2.4} fill={stroke} />
      )}
      {interactive && hoverIndex != null && (
        <circle
          cx={pointAt(hoverIndex).x}
          cy={pointAt(hoverIndex).y}
          r={2.8}
          fill="var(--surface, #fffdf8)"
          stroke={stroke}
          strokeWidth={1.6}
        />
      )}
      {/* Full-height bands rather than per-point circles: at sparkline scale the points sit a few
          pixels apart, so circular targets would overlap and the nearest one would win at random. */}
      {interactive &&
        finite.map((value, i) => (
          <rect
            key={`hit-${i}`}
            className="cfo-chart-hit"
            x={Math.max(0, (i - 0.5) * stepX)}
            y={0}
            width={Math.min(stepX, width - Math.max(0, (i - 0.5) * stepX))}
            height={height}
            fill="transparent"
            onMouseEnter={(e) => {
              setHoverIndex(i)
              show(e.clientX, e.clientY, fmt(value), labels?.[i] || '')
            }}
            onMouseMove={(e) => show(e.clientX, e.clientY, fmt(value), labels?.[i] || '')}
            onMouseLeave={() => {
              setHoverIndex(null)
              hide()
            }}
          />
        ))}
    </svg>
  )

  if (!interactive) return svg
  return (
    <CfoChartFrame
      containerRef={containerRef}
      tip={tip}
      className={className ? `${className}-frame` : undefined}
    >
      {svg}
    </CfoChartFrame>
  )
}

export default Sparkline
